import { useTheme } from "../../../../context/ThemeProvider";
import type { ChatDraftAttachment } from "../../../../context/chatAttachmentTypes";
import { useAppDispatch } from "../../../../utils/hooks";
import { setViewImage } from "../../../../store/Slices/ViewManagerSlice";
import { RxCross2 } from "react-icons/rx";
import { FaPlay } from "react-icons/fa6";
import { FiFileText } from "react-icons/fi";
import { ClipLoader } from "react-spinners";
import { Check } from "lucide-react";
import { isPreviewableImageFile } from "../../../../utils/chatDropFiles";

type Props = {
  attachments: ChatDraftAttachment[];
  gifUrl: string | null;
  onRemoveAttachment: (id: string) => void;
  onClearGif: () => void;
  uploadingId: string | null;
  uploadProgress: number;
  completingIds: Set<string>;
};

export default function AttachmentDraftStrip({
  attachments,
  gifUrl,
  onRemoveAttachment,
  onClearGif,
  uploadingId,
  uploadProgress,
  completingIds,
}: Props) {
  // @ts-ignore
  const { theme } = useTheme();
  const dispatch = useAppDispatch();

  const openPreview = (url: string) => {
    dispatch(
      setViewImage({
        show_image: true,
        image_src: [url],
        currentIndex: 0,
      }),
    );
  };

  if (attachments.length === 0 && !gifUrl) return null;

  const square =
    "relative h-[4.5rem] w-[4.5rem] shrink-0 overflow-hidden rounded-lg border border-borderColor bg-messageHead";

  return (
    <div className="flex w-full flex-wrap gap-2 px-2 pb-1 pt-2">
      {gifUrl ? (
        <div className={`group ${square}`}>
          <button
            type="button"
            aria-label="Remove gif"
            onClick={(e) => {
              e.stopPropagation();
              onClearGif();
            }}
            className="absolute right-0.5 top-0.5 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition-opacity hover:bg-black/75 group-hover:opacity-100"
          >
            <RxCross2 className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            className="relative h-full w-full"
            onClick={() => openPreview(gifUrl)}
          >
            <img
              src={gifUrl}
              alt=""
              className="h-full w-full object-cover transition-opacity group-hover:opacity-90"
            />
            <div className="pointer-events-none absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/20" />
          </button>
        </div>
      ) : null}

      {attachments.map((att) => {
        const isUp = uploadingId === att.id;
        const isDone = completingIds.has(att.id);
        const showOverlay = isUp || isDone;

        return (
          <div key={att.id} className={`group ${square}`}>
            <button
              type="button"
              aria-label="Remove"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveAttachment(att.id);
              }}
              className="absolute right-0.5 top-0.5 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition-opacity hover:bg-black/75 group-hover:opacity-100"
            >
              <RxCross2 className="h-3.5 w-3.5" />
            </button>

            <button
              type="button"
              className="relative h-full w-full"
              onClick={() => {
                if (att.kind === "image") openPreview(att.previewUrl);
                else if (
                  att.kind === "doc" &&
                  isPreviewableImageFile(att.file)
                ) {
                  openPreview(att.previewUrl);
                } else if (att.kind === "video" && att.videoThumbnail) {
                  openPreview(att.videoThumbnail);
                }
              }}
            >
              {att.kind === "image" && (
                <img
                  src={att.previewUrl}
                  alt=""
                  className="h-full w-full object-cover transition-opacity group-hover:opacity-90"
                />
              )}
              {att.kind === "video" && (
                <>
                  {att.videoThumbnail ? (
                    <img
                      src={att.videoThumbnail}
                      alt=""
                      className="h-full w-full object-cover transition-opacity group-hover:opacity-90"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-secondary">
                      <ClipLoader size={20} color="var(--theme-color)" />
                    </div>
                  )}
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <FaPlay className="h-7 w-7 rounded-full bg-black/55 p-2 text-white" />
                  </div>
                </>
              )}
              {att.kind === "doc" &&
                (isPreviewableImageFile(att.file) ? (
                  <>
                    <img
                      src={att.previewUrl}
                      alt=""
                      className="h-full w-full object-cover transition-opacity group-hover:opacity-90"
                    />
                    <div className="pointer-events-none absolute bottom-0.5 right-0.5 rounded bg-black/55 px-1 text-[9px] font-semibold uppercase text-white">
                      file
                    </div>
                  </>
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-0.5 bg-pdfBg p-1">
                    <FiFileText className="h-7 w-7 text-themeColor" />
                    <span className="line-clamp-2 w-full px-0.5 text-center text-[9px] leading-tight text-table-text">
                      {att.fileName}
                    </span>
                  </div>
                ))}
              <div className="pointer-events-none absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/15" />
            </button>

            {showOverlay && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/45">
                {isDone ? (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500 text-white shadow-lg">
                    <Check className="h-6 w-6" strokeWidth={3} />
                  </div>
                ) : (
                  <div className="relative flex h-12 w-12 items-center justify-center">
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: `conic-gradient(var(--theme-color) ${uploadProgress * 1.6}deg, ${
                          theme === "dark" ? "#333" : "#ddd"
                        } 0deg)`,
                      }}
                    />
                    <div
                      className={`relative h-9 w-9 shrink-0 rounded-full ${
                        theme === "dark" ? "bg-[#1a1a1a]" : "bg-white"
                      }`}
                      aria-hidden
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
